import type { ResultOf } from '@graphql-typed-document-node/core';
import type { Dispatch, SetStateAction } from 'react';
import type { AddItemVisibility } from 'src/components/add-item/types';
import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import IconButton from '@savvy-kit/components/icon-button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { CirclePlus } from 'lucide-react';
import { DateTime } from 'luxon';
import { DateRangeSelect } from 'src/components/performance-report/views/date-range-select';
import { EmptyPortfolioGraph } from 'src/components/portfolio/views/empty-portfolio-graph';
import PortfolioBox from 'src/components/portfolio/views/portfolio-box';
import PortfolioTitleAmount from 'src/components/portfolio/views/portfolio-title-amount';
import { GRAPH_HEIGHT } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { addHiddenItemsToNetWorth } from 'src/utils/portfolio';

import type { PerformanceTimePeriodName } from '@savvy/constants/generated/gql-enums';
import type { Value } from '@savvy/constants/generated/gql-types';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';

import { useHouseholdWriteAccess } from '../../hooks/use-household-write-access';
import { MobileDateRangeToggle } from '../performance-report/views/mobile-date-range-toggle';
import { NetWorthGraph } from '../portfolio/net-worth/graph';
import PortfolioGraphSkeleton from '../portfolio/skeleton/portfolio-graph-skeleton';

export const NetWorthGraphBoxFragment = graphql(/* GraphQL */ `
  fragment NetWorthGraphBox on NetWorthPortfolioData {
    portfolioItemGroups {
      ...NetWorthGraph
      hiddenFromClientTotalValue {
        amount
      }
      historicalValues {
        date
      }
    }
  }
`);

type NetWorthGraphProps = {
  readonly showHiddenClientAssetsInGraph: boolean;
  readonly value?: Value;
  readonly netWorthPortfolioFrag?: FragmentType<typeof NetWorthGraphBoxFragment>;
  readonly emptyPortfolio?: boolean;
  readonly setShowAddItemDialog: Dispatch<SetStateAction<AddItemVisibility | undefined>>;
};

function getFirstDateOfData(netWorthPortfolio: ResultOf<typeof NetWorthGraphBoxFragment> | undefined) {
  return netWorthPortfolio?.portfolioItemGroups
    .map((portfolioItemGroup) => portfolioItemGroup.historicalValues[0]?.date)
    .reduce((previousValue: string | null | undefined, currentValue) => {
      if (!previousValue || (currentValue && currentValue < previousValue)) {
        return currentValue;
      }
      return previousValue;
    }, null);
}

function quickDateToLookbackDays(
  quickDate: PerformanceTimePeriodName,
  netWorthPortfolio: ResultOf<typeof NetWorthGraphBoxFragment> | undefined,
) {
  let lookbackDays;
  switch (quickDate) {
    case 'all_time':
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      lookbackDays = DateTime.now().diff(DateTime.fromISO(getFirstDateOfData(netWorthPortfolio)!), 'day').days;
      break;
    case 'past_month':
      lookbackDays = DateTime.now().minus({ month: 1 }).diff(DateTime.now(), 'day').days;
      break;
    case 'past_three_months':
      lookbackDays = DateTime.now().minus({ months: 3 }).diff(DateTime.now(), 'day').days;
      break;
    case 'last_quarter':
      lookbackDays = DateTime.now().startOf('quarter').minus({ quarter: 1 }).diff(DateTime.now(), 'day').days;
      break;
    case 'past_year':
      lookbackDays = DateTime.now().minus({ year: 1 }).diff(DateTime.now(), 'day').days;
      break;
    case 'year_to_date':
      lookbackDays = DateTime.now().startOf('year').diff(DateTime.now(), 'day').days;
      break;
  }
  return Math.ceil(Math.abs(lookbackDays));
}

function renderNetWorthGraph(
  netWorthPortfolio: ResultOf<typeof NetWorthGraphBoxFragment> | undefined,
  value: Value | null,
  emptyPortfolio: boolean | undefined,
  showHiddenClientAssetsInGraph: boolean,
  quickDate: PerformanceTimePeriodName,
  isSmallScreen: boolean,
) {
  const portfolioItemGroups = netWorthPortfolio?.portfolioItemGroups;
  if (!portfolioItemGroups && !value && !isSmallScreen) {
    return <PortfolioGraphSkeleton />;
  } else if (emptyPortfolio) {
    return (
      <>
        <Text color="secondary" className="absolute left-1/2 mt-4 -translate-x-1/2">
          Connect an account to get started
        </Text>
        <div className="-ml-0.5 mb-9 flex flex-col items-center justify-end" style={{ height: GRAPH_HEIGHT }}>
          <EmptyPortfolioGraph />
        </div>
      </>
    );
  }
  return (
    <div style={{ height: GRAPH_HEIGHT }}>
      <NetWorthGraph
        portfolioItemGroupFrags={portfolioItemGroups ?? []}
        showHiddenClientAssetsInGraph={showHiddenClientAssetsInGraph}
        lookbackPeriodDays={quickDateToLookbackDays(quickDate, netWorthPortfolio)}
      />
    </div>
  );
}

function NetWorthGraphBox({
  value,
  netWorthPortfolioFrag,
  showHiddenClientAssetsInGraph,
  emptyPortfolio,
  setShowAddItemDialog,
}: NetWorthGraphProps) {
  const netWorthPortfolio = unmaskFragment(NetWorthGraphBoxFragment, netWorthPortfolioFrag);
  const portfolioItemGroups = netWorthPortfolio?.portfolioItemGroups;
  const [quickDate, setQuickDate] = useState<PerformanceTimePeriodName>('all_time');

  let netWorth = null;
  if (value?.amount) {
    if (showHiddenClientAssetsInGraph) {
      netWorth = addHiddenItemsToNetWorth(value.amount, portfolioItemGroups);
    } else {
      netWorth = parseFloat(value.amount);
    }
  }
  const isSmallScreen = useIsSmallScreen();
  const { canWrite } = useHouseholdWriteAccess();

  return (
    <PortfolioBox backgroundClassName={emptyPortfolio ? 'bg-background-light' : undefined}>
      <div className="mb-4 flex flex-row justify-between">
        <div className={cn(isSmallScreen ? 'pl-6' : 'pl-0', isSmallScreen ? 'pt-12' : 'pt-0')}>
          {portfolioItemGroups && (
            <Text
              color={isSmallScreen ? 'light' : 'default'}
              variant={isSmallScreen ? undefined : 'overline'}
              className={isSmallScreen ? 'mb-2' : 'mb-16'}
            >
              Net Worth
            </Text>
          )}
          {!portfolioItemGroups && <Skeleton className="h-4 w-20" />}
          <PortfolioTitleAmount
            amount={netWorth?.toString() || null}
            variant="h5"
            color={isSmallScreen ? 'light' : 'default'}
            displayNetWorthAsWholeNumber={emptyPortfolio}
          />
        </div>
        <div>
          {!isSmallScreen ? (
            <div>
              {portfolioItemGroups ? (
                <DateRangeSelect quickDate={quickDate} setQuickDate={setQuickDate} disabled={emptyPortfolio} />
              ) : (
                <div>
                  <Skeleton className="h-10 w-[145px]" />
                </div>
              )}
            </div>
          ) : (
            canWrite && (
              <IconButton variant="ghost" className="mr-4" onClick={() => setShowAddItemDialog('VISIBLE')}>
                <CirclePlus />
              </IconButton>
            )
          )}
        </div>
      </div>
      <div className="relative -mb-4 mt-6">
        {renderNetWorthGraph(
          netWorthPortfolio,
          value ?? null,
          emptyPortfolio,
          showHiddenClientAssetsInGraph,
          quickDate,
          isSmallScreen,
        )}
      </div>
      {isSmallScreen && (
        <MobileDateRangeToggle quickDate={quickDate} setQuickDate={setQuickDate} portfolioLevel="netWorth" />
      )}
    </PortfolioBox>
  );
}

export default NetWorthGraphBox;
